import { useState, useMemo, type ReactNode } from 'react'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { groupByCategory } from '@/lib/utils'

interface SearchableComboboxProps<T extends { category: string }> {
  items: T[]
  filterFn: (item: T, query: string) => boolean
  onSelect: (item: T) => void
  renderItem: (item: T, index: number) => ReactNode
  trigger: ReactNode
  placeholder: string
  searchLabel: string
  resultsLabel: string
  emptyText: string
  popoverWidth?: string
  scrollHeight?: string
  showSearchIcon?: boolean
  limit?: number
  itemClassName?: string
  sortFn?: (items: T[]) => T[]
  footer?: (close: () => void) => ReactNode
}

export function SearchableCombobox<T extends { category: string }>({
  items,
  filterFn,
  onSelect,
  renderItem,
  trigger,
  placeholder,
  searchLabel,
  resultsLabel,
  emptyText,
  popoverWidth = 'w-[400px]',
  scrollHeight = 'h-[280px]',
  showSearchIcon = true,
  limit = 20,
  itemClassName = 'flex w-full flex-col gap-0.5 rounded-sm p-2 text-left hover:bg-slate-100',
  sortFn,
  footer,
}: SearchableComboboxProps<T>) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    let results: T[]
    if (!search) {
      results = sortFn ? sortFn(items).slice(0, limit) : items.slice(0, limit)
    } else {
      const q = search.toLowerCase()
      results = items.filter((item) => filterFn(item, q)).slice(0, limit)
    }
    return results
  }, [items, search, filterFn, sortFn, limit])

  const grouped = useMemo(() => groupByCategory(filtered), [filtered])

  const handleSelect = (item: T) => {
    onSelect(item)
    setOpen(false)
    setSearch('')
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className={`${popoverWidth} p-0`} align="start">
        <div className="p-2">
          <div className="relative">
            {showSearchIcon && (
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            )}
            <Input
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={searchLabel}
              className={`h-8 text-xs ${showSearchIcon ? 'pl-8' : ''}`}
            />
          </div>
        </div>
        <ScrollArea className={scrollHeight} aria-label={resultsLabel}>
          <div className="p-1" role="listbox">
            {grouped.length === 0 ? (
              <p className="p-2 text-xs text-slate-500">{emptyText}</p>
            ) : (
              grouped.map((group) => (
                <div key={group.category}>
                  <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {group.category}
                  </div>
                  {group.items.map((item, i) => (
                    <button
                      key={i}
                      role="option"
                      aria-selected={false}
                      className={itemClassName}
                      onClick={() => handleSelect(item)}
                    >
                      {renderItem(item, i)}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </ScrollArea>
        {footer && (
          <div className="border-t p-2">
            {footer(() => {
              setOpen(false)
              setSearch('')
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
